// Lib
import { useEffect, useRef, useCallback, RefObject } from 'react';

// Utils
import { hasCommandModifier, hasShiftKey } from '../../utils/keyboard/keyboardUtility';

// Constants
import { KEY_CODES } from '../../utils/keyboard/keyConstants';

export enum ListDirection {
    HORIZONTAL = 'HORIZONTAL',
    VERTICAL = 'VERTICAL',
}

export interface DigitEvent {
    digit: number;
    event: KeyboardEvent;
}

export interface UseListKeyboardManagerProps {
    listDirection?: ListDirection;
    moveListFocusDown?: () => void;
    moveListFocusUp?: () => void;
    onSubmit?: (event: KeyboardEvent) => void;
    onClose?: (event: KeyboardEvent) => void;
    onDelete?: (event: KeyboardEvent) => void;
    onDigit?: (digitEvent: DigitEvent) => void;
    inputRef?: RefObject<HTMLInputElement>;
}

export interface UseListKeyboardManagerState {
    keyboardInputRef: RefObject<HTMLInputElement>;
}

const useListKeyboardManager = ({
    listDirection = ListDirection.VERTICAL,
    moveListFocusDown,
    moveListFocusUp,
    onSubmit,
    onClose,
    onDelete,
    onDigit,
    inputRef,
}: UseListKeyboardManagerProps): UseListKeyboardManagerState => {
    const keyboardInputRef = inputRef || useRef<HTMLInputElement>(null);

    const handleDigit = useCallback(
        (event: KeyboardEvent) => {
            const { keyCode } = event;

            // Not a digit
            if (keyCode < KEY_CODES[0] || keyCode > KEY_CODES[9]) return;

            if (hasCommandModifier(event)) {
                event.preventDefault();
                event.stopPropagation();
            }

            const digit = keyCode - KEY_CODES[0];

            return onDigit && onDigit({ digit, event });
        },
        [onDigit],
    );

    const handleTab = useCallback(
        (event: KeyboardEvent) => {
            event.preventDefault();
            return hasShiftKey(event) ? moveListFocusUp?.() : moveListFocusDown?.();
        },
        [moveListFocusDown, moveListFocusUp],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            switch (event.keyCode) {
                case KEY_CODES.TAB:
                    return handleTab(event);
                case KEY_CODES.DOWN_ARROW: {
                    if (listDirection === ListDirection.HORIZONTAL) return;
                    event.preventDefault();
                    return moveListFocusDown && moveListFocusDown();
                }
                case KEY_CODES.UP_ARROW: {
                    if (listDirection === ListDirection.HORIZONTAL) return;
                    event.preventDefault();
                    return moveListFocusUp && moveListFocusUp();
                }
                case KEY_CODES.RIGHT_ARROW: {
                    if (listDirection === ListDirection.VERTICAL) return;
                    event.preventDefault();
                    return moveListFocusDown && moveListFocusDown();
                }
                case KEY_CODES.LEFT_ARROW: {
                    if (listDirection === ListDirection.VERTICAL) return;
                    event.preventDefault();
                    return moveListFocusUp && moveListFocusUp();
                }
                case KEY_CODES.ESC:
                    return onClose && onClose(event);
                case KEY_CODES.ENTER:
                    return onSubmit && onSubmit(event);
                case KEY_CODES.DELETE:
                case KEY_CODES.BACKSPACE:
                    return onDelete && onDelete(event);
                default:
                    return handleDigit(event);
            }
        },
        [listDirection, moveListFocusDown, moveListFocusUp, onSubmit, onClose, onDelete, handleDigit, handleTab],
    );

    useEffect(() => {
        const listenerRef = keyboardInputRef && keyboardInputRef.current ? keyboardInputRef.current : document;

        listenerRef.addEventListener('keydown', handleKeyDown as EventListener);

        return () => {
            listenerRef.removeEventListener('keydown', handleKeyDown as EventListener);
        };
    }, [handleKeyDown, keyboardInputRef]);

    return { keyboardInputRef };
};

export default useListKeyboardManager;
